// Injects bucket-switch teaching steps before cross-bucket tour transitions.

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  getBucketById,
  getBucketForMode,
  type BucketId,
} from "@/components/shell/modes/bucketsRegistry";
import {
  FIREFLY_BUCKET_SWITCHER_TARGET_ID,
  fireflyBucketNavTargetId,
} from "./fireflyBucketNavTargets";
import type { FireflyTourStep } from "./tourTypes";

function createOpenBucketSwitcherStep(
  targetBucket: BucketId,
  anchorMode: PrimaryMode,
): FireflyTourStep {
  const label = getBucketById(targetBucket).label;
  return {
    id: `prelude-bucket-open-${targetBucket}`,
    target: {
      mode: anchorMode,
      anchorId: FIREFLY_BUCKET_SWITCHER_TARGET_ID,
      scrollIntoView: true,
    },
    deferModeNavigation: true,
    copy: {
      title: "Switch workspace",
      body: `The next steps live in the ${label} workspace. Open the workspace switcher here first (sidebar rail or mobile menu).`,
    },
    waitForClick: true,
  };
}

function createPickBucketStep(
  targetBucket: BucketId,
  anchorMode: PrimaryMode,
): FireflyTourStep {
  const label = getBucketById(targetBucket).label;
  return {
    id: `prelude-bucket-pick-${targetBucket}`,
    target: {
      mode: anchorMode,
      anchorId: fireflyBucketNavTargetId(targetBucket),
      scrollIntoView: true,
    },
    deferModeNavigation: true,
    copy: {
      title: `Choose ${label}`,
      body: `Select ${label} to load the modes and panels for this tour.`,
    },
    waitForClick: true,
  };
}

/** Expand artifact steps with bucket-switch preludes when `guideBucketSwitch` is on. */
export function expandFireflyTourSteps(
  steps: readonly FireflyTourStep[],
  startMode: PrimaryMode,
  guideBucketSwitch = true,
): FireflyTourStep[] {
  if (guideBucketSwitch === false || steps.length === 0) return [...steps];

  const out: FireflyTourStep[] = [];
  let lastBucket = getBucketForMode(startMode);

  for (const step of steps) {
    const targetBucket = getBucketForMode(step.target.mode);

    if (
      !step.skipBucketPrelude &&
      targetBucket &&
      lastBucket &&
      targetBucket !== lastBucket
    ) {
      out.push(createOpenBucketSwitcherStep(targetBucket, startMode));
      out.push(createPickBucketStep(targetBucket, startMode));
    }

    out.push(step);
    lastBucket = targetBucket ?? lastBucket;
  }

  return out;
}
